'use client';

import { useState, useRef, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Camera, Upload, Image, Loader2, Clipboard } from "lucide-react";
import { SUPPORTED_IMAGE_FORMATS } from '@/lib/supported-formats';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslations } from 'next-intl';
import { DEFAULT_MAX_IMAGE_SIZE } from '@/lib/constants';
import { processImageFile } from '@/utils/image-processing';

interface ImageToTextProps {
  onTextExtracted: (text: string, context?: string) => void;
  disabled?: boolean;
  sourceLang?: string;
  maxImageSize?: number; // Maximum width/height for image resizing
  onProcessingChange?: (isProcessing: boolean) => void;
}

export function ImageToTextButton({ onTextExtracted, disabled, sourceLang, maxImageSize = DEFAULT_MAX_IMAGE_SIZE, onProcessingChange }: ImageToTextProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasClipboardImage, setHasClipboardImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const t = useTranslations('imageToText');

  // Create accept string from supported formats
  const supportedFormatsAccept = SUPPORTED_IMAGE_FORMATS.join(',');

  useEffect(() => {
    if (isOpen) {
      checkClipboardForImage();
    }
  }, [isOpen]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      processImage(file);
    }
    setIsOpen(false);
    // Reset the input so the same file can be selected again
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleCameraCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      processImage(file);
    }
    setIsOpen(false);
    // Reset the input so user can take another photo
    if (event.target) {
      event.target.value = '';
    }
  };

  const checkClipboardForImage = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.read) {
        const clipboardItems = await navigator.clipboard.read();
        const hasImage = clipboardItems.some(item =>
          item.types.some(type => type.startsWith('image/'))
        );
        setHasClipboardImage(hasImage);
      }
    } catch (error) {
      // Clipboard access might be denied, just disable the paste option
      setHasClipboardImage(false);
    }
  };

  const handlePasteImage = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.read) {
        const clipboardItems = await navigator.clipboard.read();

        for (const item of clipboardItems) {
          const imageType = item.types.find(type => type.startsWith('image/'));
          if (imageType) {
            const blob = await item.getType(imageType);
            const file = new File([blob], 'pasted-image.png', { type: imageType });
            processImage(file);
            break;
          }
        }
      }
    } catch (error) {
      console.error('Failed to read clipboard:', error);
    }
    setIsOpen(false);
  };

  const processImage = async (file: File) => {
    const handleProcessingChange = (isProcessing: boolean) => {
      setIsProcessing(isProcessing);
      onProcessingChange?.(isProcessing);
    };

    await processImageFile({
      file,
      sourceLang,
      maxImageSize,
      onTextExtracted,
      onProcessingChange: handleProcessingChange
    });
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  const triggerCameraCapture = () => {
    cameraInputRef.current?.click();
  };

  return (
    <TooltipProvider>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                disabled={disabled || isProcessing}
              >
                {isProcessing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Image />
                )}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t('buttonTooltip')}</p>
          </TooltipContent>
        </Tooltip>

        <PopoverContent className="w-48 p-2">
          <div className="flex flex-col gap-2">
            <Button
              variant="ghost"
              className="justify-start"
              onClick={triggerFileUpload}
              disabled={isProcessing}
            >
              <Upload className="mr-2 h-4 w-4" />
              {t('uploadImage')}
            </Button>

            <Button
              variant="ghost"
              className="justify-start"
              onClick={triggerCameraCapture}
              disabled={isProcessing}
            >
              <Camera className="mr-2 h-4 w-4" />
              {t('takePhoto')}
            </Button>

            {hasClipboardImage && (
              <Button
                variant="ghost"
                className="justify-start"
                onClick={handlePasteImage}
                disabled={isProcessing}
              >
                <Clipboard className="mr-2 h-4 w-4" />
                {t('pasteImage')}
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept={supportedFormatsAccept}
        onChange={handleFileUpload}
        style={{ display: 'none' }}
      />

      <input
        ref={cameraInputRef}
        type="file"
        accept={supportedFormatsAccept}
        capture="environment"
        onChange={handleCameraCapture}
        style={{ display: 'none' }}
      />
    </TooltipProvider>
  );
}
